import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { getPortfolio, getAccount, getTradeDetails } from '../services/api';
import { createLogger } from '../utils/debugLogger';

// Create debug logger for this module
const logger = createLogger('breadcrumbs', 'Browser-style page history trail under the top status bar', false, 'INFO');

// How many recently-visited page visits to keep in the trail (including the
// current one).
export const HISTORY_CAP = 6;

/**
 * Static, single-label pages: path -> display label. Covers every
 * non-dynamic authenticated route declared in App.js's <Routes>. Labels
 * mostly mirror Navigation.js's own NAV_ITEMS labels (kept independent
 * rather than imported, since a handful of pages here -- Dashboard,
 * All Accounts, Ticker Search, Settings, etc. -- aren't top-nav links at
 * all, and drift between the two is harmless: this is just history-trail text).
 */
const STATIC_PAGE_LABELS = {
  '/dashboard': 'Dashboard',
  '/tickers': 'Tickers',
  '/all-accounts': 'All Accounts',
  '/search': 'Ticker Search',
  '/transactions': 'Transactions',
  '/trades': 'Trades',
  '/orders': 'Orders',
  '/plan': 'Plan',
  '/notes': 'Notes',
  '/wisdom': 'Wisdom',
  '/activity-log': 'Activity Log',
  '/settings': 'Settings',
  '/admin-settings': 'Admin Settings',
  '/profile': 'Profile',
  '/watchlist': 'Watchlist',
  '/ants': 'Ants',
  '/new-ipos': 'New IPOs',
  '/etfs': 'ETFs',
  '/stopouts': 'Stopouts',
  '/leaders': 'Leaders',
  '/vcp': 'VCP',
  '/basing': 'Basing',
  '/eps': 'GapUPs',
  '/athigh': 'AT High',
  '/turnarounds': 'Turnarounds',
  '/cyclicals': 'Cyclicals',
  '/gleaders': 'GLeaders',
  '/inverses': 'Inverses',
  '/steroids': 'Steroids',
  '/hvol': 'HVol',
  '/minervini': 'Minervini',
  '/strategies': 'Strategies',
  '/screen': 'Screen',
  '/themes': 'Themes',
  '/readylist': 'Ready List',
  '/livermore': 'Livermore',
  '/play': 'Play',
};

// In-memory cache for resolved dynamic-route names (portfolio/account/trade),
// so revisiting something already visited this session doesn't re-fetch just
// to rebuild the same history-trail label. Cleared on full reload.
const nameCache = new Map();

async function cached(key, fetcher) {
  if (nameCache.has(key)) return nameCache.get(key);
  const value = await fetcher();
  nameCache.set(key, value);
  return value;
}

/**
 * Resolve a page's own display label synchronously, for every route whose
 * label doesn't require an API call: the static single-label pages, plus
 * the three dynamic routes whose param IS the display name already (Notes'
 * ticker symbol, GLeaders'/Themes' theme name) -- no fetch needed, just
 * decodeURIComponent.
 *
 * Returns null for routes not covered here (either unknown, or one of the
 * three routes getAsyncLabelResolver handles instead).
 */
export function getSyncLabel(pathname) {
  const staticLabel = STATIC_PAGE_LABELS[pathname];
  if (staticLabel) return staticLabel;

  const notesMatch = matchPath({ path: '/notes/:symbol', end: true }, pathname);
  if (notesMatch) return decodeURIComponent(notesMatch.params.symbol);

  const gleadersMatch = matchPath({ path: '/gleaders/:themeName', end: true }, pathname);
  if (gleadersMatch) return decodeURIComponent(gleadersMatch.params.themeName);

  const themesMatch = matchPath({ path: '/themes/:themeName', end: true }, pathname);
  if (themesMatch) return decodeURIComponent(themesMatch.params.themeName);

  return null;
}

/**
 * Resolve a page's own display label for the three routes that need a real
 * entity name fetched from the API. Returns a zero-arg async function that
 * resolves to the label, or null if `pathname` doesn't match any of them.
 */
export function getAsyncLabelResolver(pathname) {
  const portfolioMatch = matchPath({ path: '/portfolio/:portfolioId', end: true }, pathname);
  if (portfolioMatch) {
    const { portfolioId } = portfolioMatch.params;
    return () => cached(`portfolio:${portfolioId}`, async () => {
      const { data } = await getPortfolio(portfolioId);
      return data?.name;
    })
      .then(name => name || `Portfolio ${portfolioId}`)
      .catch(error => {
        logger.error('Failed to resolve portfolio name for history trail', error);
        return `Portfolio ${portfolioId}`;
      });
  }

  const accountMatch = matchPath({ path: '/account/:accountId', end: true }, pathname);
  if (accountMatch) {
    const { accountId } = accountMatch.params;
    return () => cached(`account:${accountId}`, async () => {
      const { data } = await getAccount(accountId);
      return data?.name;
    })
      .then(name => name || `Account ${accountId}`)
      .catch(error => {
        logger.error('Failed to resolve account name for history trail', error);
        return `Account ${accountId}`;
      });
  }

  const tradeMatch = matchPath({ path: '/trades/:tradeId', end: true }, pathname);
  if (tradeMatch) {
    const { tradeId } = tradeMatch.params;
    return () => cached(`trade:${tradeId}`, async () => {
      const { data } = await getTradeDetails(tradeId);
      return data?.ticker_symbol;
    })
      .then(symbol => symbol || `Trade ${tradeId}`)
      .catch(error => {
        logger.error('Failed to resolve trade name for history trail', error);
        return `Trade ${tradeId}`;
      });
  }

  return null;
}

// Keys used in react-router `state` (via <Link state={...}>) to mark a
// navigation as having originated from clicking a trail entry itself, and
// which entry -- as opposed to any other link in the app (top nav, in-page
// links, etc.) landing on a path the trail happens to already contain.
// Namespaced/prefixed so this never collides with another page's own use of
// location.state (e.g. GLeaderDetails.js/ThemeDetails.js stash their own
// themeName/themeId there).
export const BREADCRUMB_NAV_FLAG = '__fromBreadcrumbTrail';
export const BREADCRUMB_NAV_INDEX = '__breadcrumbTrailIndex';

/**
 * Push {path, label} onto a visit-history stack -- every visit is appended
 * as its own entry, even a repeat visit to a page already elsewhere in the
 * trail, rather than truncating back to (or deduplicating against) the
 * earlier visit. Just a capped visit log: drops the oldest entry once the
 * stack would exceed `cap`.
 */
export function pushHistoryEntry(stack, path, label, cap = HISTORY_CAP) {
  const next = [...stack, { path, label }];
  return next.length > cap ? next.slice(next.length - cap) : next;
}

/**
 * Apply one navigation to the visit-history stack. `navState` is the
 * react-router location.state for this specific navigation:
 *  - If it carries BREADCRUMB_NAV_FLAG (i.e. the user clicked one of the
 *    trail's own links) *and* BREADCRUMB_NAV_INDEX still points at an entry
 *    for this same path, truncate the trail back to that entry -- a real
 *    "go back to here" action.
 *  - Otherwise (any other link, nav bar, browser back/forward, direct URL
 *    entry, ...), just stack a new entry via pushHistoryEntry, even if this
 *    path already appears elsewhere in the trail.
 */
export function applyHistoryNavigation(stack, path, label, navState, cap = HISTORY_CAP) {
  const truncateIndex = navState && navState[BREADCRUMB_NAV_FLAG] === true ? navState[BREADCRUMB_NAV_INDEX] : null;
  if (
    typeof truncateIndex === 'number' &&
    truncateIndex >= 0 &&
    truncateIndex < stack.length &&
    stack[truncateIndex].path === path
  ) {
    return stack.slice(0, truncateIndex + 1);
  }
  return pushHistoryEntry(stack, path, label, cap);
}

/**
 * Resolve `path`'s display label (sync or async) and apply it to the
 * history stack via `combine(prevStack, path, label)` -- shared by the
 * location-change effect (which stacks/truncates via applyHistoryNavigation)
 * and the reset button (which replaces the whole stack with just this one
 * entry). Returns a cancel() function for the async case, matching the
 * effect-cleanup contract; a no-op for the sync case / unknown routes.
 */
function applyResolvedLabel(path, setHistoryStack, combine) {
  const syncLabel = getSyncLabel(path);
  const resolveAsync = syncLabel === null ? getAsyncLabelResolver(path) : null;

  if (syncLabel === null && !resolveAsync) {
    // Unknown route (login/register/404) -- nothing to add to history.
    return () => {};
  }

  setHistoryStack(prev => combine(prev, path, syncLabel || 'Loading…'));

  if (resolveAsync) {
    let cancelled = false;
    resolveAsync().then(label => {
      if (!cancelled) {
        setHistoryStack(prev => prev.map(entry => (entry.path === path ? { ...entry, label } : entry)));
      }
    });
    return () => { cancelled = true; };
  }

  return () => {};
}

function Breadcrumbs() {
  const location = useLocation();
  const [historyStack, setHistoryStack] = useState([]);

  useEffect(() => {
    const path = location.pathname;
    const navState = location.state;
    return applyResolvedLabel(path, setHistoryStack, (prev, p, l) => applyHistoryNavigation(prev, p, l, navState));
  }, [location.pathname, location.state]);

  const handleReset = () => {
    // Replaces the whole stack with just the current page, rather than
    // clearing to nothing -- there's always something to show once you're
    // on an authenticated page, and starting the trail fresh "from here" is
    // more useful than the bar simply vanishing until the next navigation.
    applyResolvedLabel(location.pathname, setHistoryStack, (prev, p, l) => [{ path: p, label: l }]);
  };

  if (historyStack.length === 0) {
    return null;
  }

  return (
    <nav aria-label="breadcrumb" className="breadcrumb-bar py-1">
      {/* Same react-bootstrap <Container> Navigation.js wraps both its
          nav-links Navbar and its status bar in, so this trail's content
          lines up with theirs. */}
      <Container className="d-flex align-items-center">
        {historyStack.length > 1 && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 me-2 text-muted breadcrumb-reset-btn"
            onClick={handleReset}
            title="Clear page history"
            aria-label="Clear page history"
          >
            <i className="bi bi-arrow-counterclockwise" />
          </button>
        )}
        <ol className="breadcrumb mb-0 small">
          {historyStack.map((entry, index) => {
            const isLast = index === historyStack.length - 1;
            const label = entry.path === '/dashboard' ? (
              <><i className="bi bi-house-door-fill me-1" />{entry.label}</>
            ) : entry.label;
            return (
              <li
                // index, not entry.path alone -- the same page can now
                // appear more than once in the trail (a repeat visit is
                // stacked, not deduplicated/truncated back to).
                key={`${entry.path}-${index}`}
                className={`breadcrumb-item${isLast ? ' active' : ''}`}
                aria-current={isLast ? 'page' : undefined}
              >
                {isLast ? label : (
                  // state flags this navigation as "clicked a trail entry to
                  // go back to it" -- see applyHistoryNavigation -- vs. any
                  // other link elsewhere in the app landing on this same
                  // path, which should just stack a new entry instead.
                  <Link to={entry.path} state={{ [BREADCRUMB_NAV_FLAG]: true, [BREADCRUMB_NAV_INDEX]: index }}>
                    {label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </Container>
    </nav>
  );
}

export default Breadcrumbs;
